/**
 * Admin Email Suppressions Data Hooks
 *
 * React Query hooks for the do-not-email list shown at the bottom of the
 * Messages tab: paged/searchable list (previous page kept while refetching),
 * add (manual entry) and remove mutations — both invalidate the list.
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  adminEmailSuppressionAdd,
  adminEmailSuppressionDelete,
  adminEmailSuppressionsList,
} from "../utils/adminApi";

export const ADMIN_EMAIL_SUPPRESSIONS_QUERY_KEY = ["admin", "email-suppressions"];
export const ADMIN_EMAIL_SUPPRESSIONS_LIST_QUERY_KEY = [...ADMIN_EMAIL_SUPPRESSIONS_QUERY_KEY, "list"];

/** One page of suppressed addresses; `q` narrows on the email (icontains). */
export const useAdminEmailSuppressions = ({ q = "", page = 1, page_size = 25, enabled = true } = {}) => {
  return useQuery({
    queryKey: [...ADMIN_EMAIL_SUPPRESSIONS_LIST_QUERY_KEY, { q, page, page_size }],
    queryFn: () => adminEmailSuppressionsList({ q, page, page_size }),
    enabled,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });
};

const useInvalidateSuppressions = () => {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: ADMIN_EMAIL_SUPPRESSIONS_LIST_QUERY_KEY });
  };
};

/** Add (or re-note) a manual suppression: `{ email, notes? }`. */
export const useAddAdminEmailSuppression = () => {
  const invalidate = useInvalidateSuppressions();
  return useMutation({
    mutationFn: (payload) => adminEmailSuppressionAdd(payload),
    onSuccess: async () => {
      await invalidate();
    },
  });
};

/** Remove a suppression by id (the address becomes mailable again). */
export const useRemoveAdminEmailSuppression = () => {
  const invalidate = useInvalidateSuppressions();
  return useMutation({
    mutationFn: (id) => adminEmailSuppressionDelete(id),
    onSuccess: async () => {
      await invalidate();
    },
  });
};
